import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useGetReleaseKitQuery, useGetReleaseKitsQuery } from '../../../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../../../redux/api/fymAPI.types';
import { PromoteMediaDraft } from '../../../../../music24/promote/draftStore';
import { PickerFilter, pickerFiles } from '../../../../../music24/promote/composer/picker/pickerFiles';
import { usePickerUpload } from '../../../../../music24/promote/composer/picker/usePickerUpload';
import { MediaBlockSongFiles, mediaBlockSongTiles, mediaBlockTileCount } from './mediaBlockTiles';

interface UseMediaBlockPickerProps {
  accountId: number;
  /** The picked song; undefined is "All songs". */
  song: string | undefined;
  items: CatalogItem[];
  /** True while the catalog loads: the song is not known yet, so nothing is read. */
  isSongLoading: boolean;
  onUploaded: (media: PromoteMediaDraft) => void;
}

/**
 * The files that a Media block can show: the cover of each song, and the Composer picker rules
 * (`pickerFiles`: the newest ready revision of each asset type and variant, and of each reel
 * length). One song reads its release kit; "All songs" reads the kit list with its ready files
 * (`GET release-kits?include=assets`, the newest 50 songs). Then the Images / Videos filter, and
 * the Composer upload (an image through `POST files`, a video through the presigned post media
 * upload).
 */
export function useMediaBlockPicker({ accountId, song, items, isSongLoading, onUploaded }: UseMediaBlockPickerProps) {
  const intl = useIntl();
  const [filter, setFilter] = useState<PickerFilter>('all');
  const isAllSongs = song === undefined;
  const kit = useGetReleaseKitQuery(
    { accountId, catalogItemUuid: song ?? '' },
    { skip: isSongLoading || song === undefined }
  );
  const kits = useGetReleaseKitsQuery({ accountId, include: 'assets' }, { skip: isSongLoading || !isAllSongs });

  const songs = useMemo<MediaBlockSongFiles[]>(() => {
    if (song !== undefined) {
      return [{ catalogItemUuid: song, item: items.find((item) => item.uuid === song), files: pickerFiles(kit.data) }];
    }
    const summaries = new Map((kits.data?.kits ?? []).map((summary) => [summary.catalogItemUuid, summary] as const));
    return items.map((item) => {
      const summary = summaries.get(item.uuid);
      return {
        catalogItemUuid: item.uuid,
        item,
        files: summary === undefined ? [] : pickerFiles({ assets: summary.assets ?? [], reels: summary.reels ?? [] }),
      };
    });
  }, [items, kit.data, kits.data, song]);

  const tileCount = useMemo(() => mediaBlockTileCount(intl, songs), [intl, songs]);
  const songTiles = useMemo(() => mediaBlockSongTiles(intl, songs, filter), [filter, intl, songs]);
  const upload = usePickerUpload({ accountId, onUploaded });
  const active = isAllSongs ? kits : kit;

  return {
    filter,
    setFilter,
    isAllSongs,
    songTiles,
    tileCount,
    isLoading: isSongLoading || active.isLoading,
    error: active.error,
    refetch: active.refetch,
    upload,
  };
}

export type MediaBlockPicker = ReturnType<typeof useMediaBlockPicker>;
